import React, {
  createContext,
  forwardRef,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  AspectRatio,
  Box,
  CloseButton,
  Flex,
  IconButton,
} from "@chakra-ui/react";
import { FiArrowUp } from "react-icons/fi";
import { HtmlPortalNode, InPortal } from "react-reverse-portal";
import { useInView } from "react-intersection-observer";

const ASPECT_RATIO = 16 / 9;
const PIP_OFFSET = 4;
const PIP_WIDTH = 300;
const PIP_TOOLBAR_HEIGHT = 7;

export const PortalContext = createContext<HtmlPortalNode | null>(null);

type EmbedRatioProps = {
  pip?: boolean;
  ratio?: number;
  title: string;
  src: string;
  name?: string;
  className?: string;
  onPipClose?: () => void;
};

type EmbedRatioHandle = {
  closePip: () => void;
};

export const EmbedRatio = forwardRef<EmbedRatioHandle, EmbedRatioProps>(
  (
    { pip: pipEnabled, ratio = ASPECT_RATIO, title, onPipClose, ...props },
    ref
  ) => {
    const [pip, setPip] = useState(false);

    const { ref: inViewRef, inView } = useInView({
      threshold: 0.25,
      onChange: (inView) => pipEnabled && !inView && !pip && setPip(true),
    });

    const isPip = useMemo(() => !inView && pip, [inView, pip]);

    const portalNode = useContext(PortalContext);

    useImperativeHandle(
      ref,
      () => ({
        closePip: () => setPip(false),
      }),
      []
    );

    return (
      <>
        <AspectRatio ref={inViewRef} ratio={ratio} bg="bg">
          <div>
            <Box
              sx={
                isPip
                  ? {
                      pos: "fixed",
                      bottom: PIP_OFFSET,
                      right: PIP_OFFSET,
                      w: PIP_WIDTH,
                      zIndex: 2,
                    }
                  : { boxSize: "full" }
              }
            >
              {isPip && (
                // picture-in-picture frame
                <Flex
                  align="center"
                  justify="flex-end"
                  roundedTop="sm"
                  h={PIP_TOOLBAR_HEIGHT}
                  bg="bgSecondary"
                >
                  <IconButton
                    aria-label="Back to top"
                    mr="1"
                    variant="ghost"
                    size="xs"
                    fontSize="md"
                    icon={<FiArrowUp />}
                    onClick={() => window.scrollTo(0, 0)}
                  />
                  <CloseButton
                    size="sm"
                    onClick={() => {
                      onPipClose?.();
                      setPip(false);
                    }}
                  />
                </Flex>
              )}
              <AspectRatio ratio={ratio}>
                <iframe
                  title={title}
                  frameBorder={0}
                  allowFullScreen
                  {...props}
                />
              </AspectRatio>
            </Box>
          </div>
        </AspectRatio>
        {/**
         * This conditionally sends a spacer to the parent component using a
         * ✨ reverse portal ✨ so that the pip fits in nicely under the pagination
         *
         * See https://github.com/httptoolkit/react-reverse-portal
         */}
        {portalNode && (
          <InPortal node={portalNode}>
            {pip && (
              <Box
                boxSizing="content-box"
                pt={PIP_TOOLBAR_HEIGHT}
                h={PIP_WIDTH / ratio}
                mb={PIP_OFFSET}
              />
            )}
          </InPortal>
        )}
      </>
    );
  }
);

EmbedRatio.displayName = "EmbedRatio";
